import { useState } from "react";
import { Zap, Shield, DollarSign, Clock, Tag, FileText, Settings2, ChevronDown, Truck, Send, Globe2, Lock, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import BidDeadlinePicker from "./BidDeadlinePicker";

export type LRDirectPostType = "load" | "quote";
export type LRDirectVisibility = "open_market" | "private_only" | "both";

/** Exported for the guided wizard extras step — same handling chip set as the form. */
export const HANDLING_OPTIONS = [
  "Drop & Hook",
  "No Touch",
  "Hazmat",
  "Tarping Required",
  "Driver Assist",
  "Temperature Controlled",
];

export const POST_TYPE_META = {
  load: { label: "LOAD", Icon: Truck, gradient: "linear-gradient(135deg, hsl(174 98% 30%), hsl(174 90% 22%))" },
  quote: { label: "QUOTE", Icon: DollarSign, gradient: "linear-gradient(135deg, hsl(38 95% 50%), hsl(32 92% 40%))" },
};

interface DirectPublishCardProps {
  targetRateMin: string;
  targetRateMax: string;
  bidDeadline: string;
  bidDeadlineError?: string;
  priorityTag: "standard" | "priority";
  shipperNotes: string;
  appointmentType: "fcfs" | "appointment";
  handlingRequirements: string[];
  onTargetRateMinChange: (v: string) => void;
  onTargetRateMaxChange: (v: string) => void;
  onBidDeadlineChange: (v: string) => void;
  onPriorityTagChange: (v: "standard" | "priority") => void;
  onShipperNotesChange: (v: string) => void;
  onAppointmentTypeChange: (v: "fcfs" | "appointment") => void;
  onHandlingRequirementsChange: (v: string[]) => void;
  saveAsTemplate?: boolean;
  onSaveAsTemplateChange?: (v: boolean) => void;
  templateName?: string;
  onTemplateNameChange?: (v: string) => void;
  suggestedTemplateName?: string;
  templateAlreadySavedFromId?: string | null;
  onPublish: (visibility: LRDirectVisibility, postType: LRDirectPostType) => void;
  postType: LRDirectPostType;
  onPostTypeChange: (v: LRDirectPostType) => void;
  visibility: LRDirectVisibility;
  onVisibilityChange: (v: LRDirectVisibility) => void;
  privateNetworkAvailable?: boolean;
  privateNetworkMemberCount?: number;
  isSubmitting: boolean;
  totalMiles: number | null;
}

export default function DirectPublishCard({
  targetRateMin,
  targetRateMax,
  bidDeadline,
  bidDeadlineError,
  priorityTag,
  shipperNotes,
  appointmentType,
  handlingRequirements,
  onTargetRateMinChange,
  onTargetRateMaxChange,
  onBidDeadlineChange,
  onPriorityTagChange,
  onShipperNotesChange,
  onAppointmentTypeChange,
  onHandlingRequirementsChange,
  onSaveAsTemplateChange,
  saveAsTemplate = false,
  templateName = "",
  onTemplateNameChange,
  suggestedTemplateName = "",
  templateAlreadySavedFromId,
  onPublish,
  postType,
  onPostTypeChange,
  visibility,
  onVisibilityChange,
  privateNetworkAvailable = false,
  privateNetworkMemberCount = 0,
  isSubmitting,
  totalMiles,
}: DirectPublishCardProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const rateMin = parseFloat(targetRateMin.replace(/,/g, "")) || 0;
  const rateMax = parseFloat(targetRateMax.replace(/,/g, "")) || 0;

  // Active accent for the whole card + visibility buttons.
  // Quote (any visibility) → orange. Load + open → yellow. Load + private/both → teal/green.
  const accentToken =
    postType === "quote"
      ? "--accent-quote"
      : visibility === "open_market"
        ? "--accent-open"
        : "--accent-private";
  const accentStyle = { ["--active-accent" as any]: `var(${accentToken})` } as React.CSSProperties;

  const toggleHandling = (req: string) => {
    if (handlingRequirements.includes(req)) {
      onHandlingRequirementsChange(handlingRequirements.filter(r => r !== req));
    } else {
      onHandlingRequirementsChange([...handlingRequirements, req]);
    }
  };

  return (
    <div
      className="rounded-xl border bg-card flex flex-col transition-colors"
      style={{
        ...accentStyle,
        boxShadow: "var(--shadow-card)",
        borderColor: "hsl(var(--active-accent) / 0.55)",
        backgroundColor: "hsl(var(--active-accent) / 0.03)",
      }}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Zap className="h-3 w-3 flex-shrink-0" style={{ color: "hsl(var(--active-accent))" }} />
            <span className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color: "hsl(var(--active-accent))" }}>
              LR Direct — {postType === "load" ? "Load" : "Quote"} Bid Settings
            </span>
          </div>
          {(() => {
            const meta = POST_TYPE_META[postType];
            const Icon = meta.Icon;
            return (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white shadow-sm flex-shrink-0"
                style={{ background: meta.gradient }}
              >
                <Icon className="h-3 w-3" />
                {meta.label}
              </span>
            );
          })()}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {visibility === "open_market"
            ? "Configure how brokers will bid on this load. On open-market posts, your company identity stays hidden until you award."
            : "Configure how brokers in your private network will bid on this load."}
        </p>
      </div>

      {/* Target Rate (optional) */}
      <div className="px-4 py-3 border-b border-border space-y-2">
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-3 w-3 text-muted-foreground" />
          <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Target Rate <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span>
          </Label>
        </div>
        <div className="flex items-center rounded-md overflow-hidden border border-input max-w-[200px]">
          <span className="px-2 py-1.5 text-xs font-medium bg-muted text-muted-foreground border-r border-input">$</span>
          <Input
            className="border-0 focus-visible:ring-0 text-sm font-bold h-8"
            value={targetRateMax}
            onChange={(e) => onTargetRateMaxChange(e.target.value)}
            placeholder="0"
          />
        </div>
        {totalMiles && rateMax > 0 && (
          <span className="text-[10px] text-muted-foreground">
            Target: ${(rateMax / totalMiles).toFixed(2)}/mi
          </span>
        )}
      </div>

      {/* Bid Deadline */}
      <div className={cn("px-4 py-3 border-b border-border space-y-1.5", bidDeadlineError && "bg-destructive/5")}>
        <div className="flex items-center gap-1.5">
          <Clock className={cn("h-3 w-3", bidDeadlineError ? "text-destructive" : "text-muted-foreground")} />
          <Label className={cn("text-[10px] font-medium uppercase tracking-wide", bidDeadlineError ? "text-destructive" : "text-muted-foreground")}>
            Bid Deadline <span className="text-destructive">*</span>
          </Label>
        </div>
        <div className={cn(bidDeadlineError && "rounded-md ring-1 ring-destructive")}>
          <BidDeadlinePicker
            value={bidDeadline}
            onChange={onBidDeadlineChange}
          />
        </div>
        {bidDeadlineError && (
          <p className="text-[10px] font-medium text-destructive">{bidDeadlineError}</p>
        )}
      </div>


      {/* Priority Tag */}
      <div className="px-4 py-3 border-b border-border space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Tag className="h-3 w-3 text-muted-foreground" />
          <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Priority
          </Label>
        </div>
        <RadioGroup
          value={priorityTag}
          onValueChange={(v) => onPriorityTagChange(v as "standard" | "priority")}
          className="flex gap-3"
        >
          <label className="flex items-center gap-1.5 cursor-pointer">
            <RadioGroupItem value="standard" />
            <span className="text-xs text-foreground">Standard</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <RadioGroupItem value="priority" />
            <span className="text-xs font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>⚡ Priority</span>
          </label>
        </RadioGroup>
      </div>

      {/* Advanced Details — Collapsible */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full px-4 py-2.5 border-b border-border flex items-center justify-between hover:bg-muted/50 transition-colors">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              Advanced Details (optional)
            </span>
            <ChevronDown className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              advancedOpen && "rotate-180"
            )} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* Shipper Notes */}
          <div className="px-4 py-3 border-b border-border space-y-1.5">
            <div className="flex items-center gap-1.5">
              <FileText className="h-3 w-3 text-muted-foreground" />
              <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                Notes to Brokers
              </Label>
            </div>
            <Textarea
              className="min-h-[60px] text-xs resize-none"
              placeholder="Any requirements or preferences for brokers..."
              value={shipperNotes}
              onChange={(e) => onShipperNotesChange(e.target.value)}
              maxLength={500}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Post as: LOAD vs QUOTE */}
      <div className="px-4 py-3 border-b border-border space-y-1.5">
        <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
          Post as
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onPostTypeChange("load")}
            className={cn(
              "flex items-center justify-center gap-1.5 h-9 rounded-md border text-xs font-bold uppercase tracking-wide transition-all",
              postType === "load"
                ? "border-transparent text-white shadow-sm"
                : "border-border bg-background text-muted-foreground hover:bg-muted",
            )}
            style={postType === "load" ? {
              background: "linear-gradient(135deg, hsl(174 98% 30%), hsl(174 90% 22%))",
            } : undefined}
          >
            <Truck className="h-3.5 w-3.5" />
            Load
          </button>
          <button
            type="button"
            onClick={() => onPostTypeChange("quote")}
            className={cn(
              "flex items-center justify-center gap-1.5 h-9 rounded-md border text-xs font-bold uppercase tracking-wide transition-all",
              postType === "quote"
                ? "border-transparent text-white shadow-sm"
                : "border-border bg-background text-muted-foreground hover:bg-muted",
            )}
            style={postType === "quote" ? {
              background: "linear-gradient(135deg, hsl(38 95% 50%), hsl(32 92% 40%))",
            } : undefined}
          >
            <DollarSign className="h-3.5 w-3.5" />
            Quote
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground leading-snug">
          {postType === "load"
            ? "Brokers bid to cover this shipment."
            : "Pricing request — brokers submit a price quote, no commitment."}
        </p>
      </div>

      {/* Visibility */}
      <div className="px-4 py-3 border-b border-border space-y-1.5">
        <Label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
          Visibility
        </Label>
        <div className="grid grid-cols-3 gap-1.5">
          {([
            { value: "private_only" as const, label: "Private", icon: Lock, disabled: !privateNetworkAvailable, token: "--accent-private" },
            { value: "open_market" as const, label: "Open Market", icon: Globe2, disabled: false, token: "--accent-open" },
            { value: "both" as const, label: "Both", icon: Layers, disabled: !privateNetworkAvailable, token: "--accent-private" },
          ]).map(({ value, label, icon: Icon, disabled, token }) => {
            const isSelected = visibility === value;
            // When Quote mode is on, selected visibility button follows the quote accent.
            const selectedToken = postType === "quote" ? "--accent-quote" : token;
            return (
              <button
                key={value}
                type="button"
                disabled={disabled}
                onClick={() => onVisibilityChange(value)}
                title={disabled ? "Set up your private network first" : undefined}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 h-11 rounded-md border text-[10px] font-semibold uppercase tracking-wide transition-all",
                  !isSelected && "border-border bg-background text-muted-foreground hover:bg-muted",
                  disabled && "opacity-50 cursor-not-allowed hover:bg-background",
                )}
                style={isSelected ? {
                  borderColor: `hsl(var(${selectedToken}))`,
                  backgroundColor: `hsl(var(${selectedToken}) / 0.12)`,
                  color: `hsl(var(${selectedToken}))`,
                } : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            );
          })}
        </div>
        {visibility !== "open_market" && privateNetworkAvailable && privateNetworkMemberCount > 0 && (
          <p className="text-[10px] text-muted-foreground">
            {privateNetworkMemberCount} broker{privateNetworkMemberCount === 1 ? "" : "s"} in your private network.
          </p>
        )}
        {visibility === "both" && (
          <p className="text-[10px] text-muted-foreground leading-snug">
            Both sends to your entire private network plus the open market.
          </p>
        )}
      </div>


      {/* Privacy note — open market only */}
      {visibility === "open_market" && (
        <div className="px-4 py-2 border-b border-border">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Shield className="h-3 w-3 flex-shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }} />
            <span>On open-market loads, your company identity is hidden from brokers until you award.</span>
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="px-4 py-4 space-y-2">
        <Button
          onClick={() => onPublish(visibility, postType)}
          disabled={isSubmitting}
          className="w-full h-11 text-sm font-bold uppercase tracking-wide transition-all duration-150 hover:shadow-lg active:scale-[0.98] gap-2"
          style={postType === "quote" ? {
            background: "linear-gradient(135deg, hsl(38 95% 50%), hsl(32 92% 40%))",
            color: "#ffffff",
            boxShadow: "0 4px 20px hsl(38 95% 50% / 0.3)",
          } : {
            background: "linear-gradient(135deg, hsl(174 98% 30%), hsl(174 90% 22%))",
            color: "#ffffff",
            boxShadow: "0 4px 20px hsl(174 98% 30% / 0.35)",
          }}
        >
          <Send className="h-4 w-4" />
          {isSubmitting
            ? "POSTING..."
            : `POST ${postType === "quote" ? "QUOTE" : "LOAD"} — ${
                visibility === "private_only" ? "PRIVATE" : visibility === "both" ? "PRIVATE + OPEN" : "OPEN MARKET"
              }`}
        </Button>
        {/* Save as template */}
        <div className="rounded-md border border-dashed border-border p-2.5 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-brand-teal-deep cursor-pointer"
              checked={saveAsTemplate}
              disabled={isSubmitting || !!templateAlreadySavedFromId}
              onChange={(e) => {
                const next = e.target.checked;
                onSaveAsTemplateChange?.(next);
                // Auto-populate the name the moment the box is checked, if the
                // shipper hasn't typed one yet. Still fully editable below.
                if (next && !templateName.trim() && suggestedTemplateName) {
                  onTemplateNameChange?.(suggestedTemplateName);
                }
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-foreground leading-tight">
                Save as template
              </div>
              <p className="text-[10px] text-muted-foreground leading-snug">
                {templateAlreadySavedFromId
                  ? "Posting from a saved template — usage count will update."
                  : "Reuse this lane, equipment, and notes for one-click posting."}
              </p>
            </div>
          </label>
          {saveAsTemplate && !templateAlreadySavedFromId && (
            <Input
              className="h-8 text-xs"
              placeholder={suggestedTemplateName || "Template name"}
              value={templateName}
              onChange={(e) => onTemplateNameChange?.(e.target.value)}
              maxLength={80}
            />
          )}
        </div>
      </div>
    </div>
  );
}
